import {
  Identifiable,
  ProjectResource,
  Resource,
  ResourcesListingFilter,
  ResourceTypes,
  organizationalUnit as organizationalUnitMock,
  resource as resourceMock,
  role as roleMock,
} from "@planzone/dto";

type ListedSubordinate = {
  uuid: string;
  name: string;
  avatarUrl: string | null;
  type: ResourceTypes;
  profession: string | null;
};

type ListedResource = {
  uuid: string;
  createdDate: string | number | null;
  name: string;
  type: ResourceTypes;
  profession: string | null;
  description: string | null;
  affiliation: "INTERNAL" | "EXTERNAL" | null;
  avatarUrl: string | null;
  deactivated: boolean | null;
  invitationAccepted: boolean;
  tags: string[];
  role: { uuid: string; name: string } | null;
  attributes: { [key: string]: string };
  organizationalUnit: { uuid: string; name: string } | null;
  costProfile?: {
    uuid: string;
    name: string;
    description: string;
    cost: number;
  } | null;
  subordinates: ListedSubordinate[];
};

type ListingPage = {
  size: number;
  totalElements: number;
  totalPages: number;
  number: number;
};

// Same shape as the OBS tree resources. Description and affiliation are filled in: the inline edition
// sends them back in its PATCH.
function toListedResource(item: ListedResource): Identifiable<Resource> {
  return {
    ...resourceMock,
    uuid: item.uuid,
    createdDate:
      item.createdDate === null ? "" : new Date(item.createdDate).toISOString(),
    name: item.name,
    type: item.type,
    profession: item.profession ?? "",
    description: item.description ?? "",
    affiliation: item.affiliation,
    avatarUrl: item.avatarUrl ?? "",
    deactivated: item.deactivated ?? false,
    invitationAccepted: item.invitationAccepted,
    tags: item.tags.map((name) => ({ name })),
    role: item.role
      ? { ...roleMock, uuid: item.role.uuid, name: item.role.name }
      : undefined,
    attributes: item.attributes,
    organizationalUnit: item.organizationalUnit
      ? { ...organizationalUnitMock, ...item.organizationalUnit }
      : null,
    costProfile: item.costProfile ?? null,
    subordinates: item.subordinates.map((subordinate) => ({
      ...resourceMock,
      ...subordinate,
      avatarUrl: subordinate.avatarUrl ?? "",
      profession: subordinate.profession ?? "",
    })),
  };
}

// The picked resources and the members of the picked groups, like the OBS tree. Project groups list
// project resources.
// The picked units with their subtrees, less the unchecked ones under them, like the OBS filter.
function toOrganizationalUnitsCriteria(filter: ResourcesListingFilter) {
  const included = (filter.organizationalUnits ?? [])
    .filter(({ isExcluded }) => !isExcluded)
    .map(({ uuid }) => uuid);
  const excluded = (filter.organizationalUnits ?? [])
    .filter(({ isExcluded }) => isExcluded)
    .map(({ uuid }) => uuid);

  return included.length > 0
    ? {
        organizationalUnitUuids: included,
        ...(excluded.length > 0 && {
          excludedOrganizationalUnitUuids: excluded,
        }),
      }
    : {};
}

function toListingBody(filter: ResourcesListingFilter) {
  return {
    page: filter.page,
    size: filter.size,
    sort: filter.sort,
    inactiveOnly: !!filter.inactiveOnly,
    activeOnly: !!filter.activeOnly,
    ...toOrganizationalUnitsCriteria(filter),
    ...(!!filter.name?.trim() && { name: filter.name.trim() }),
    ...(filter.resourceTypes &&
      filter.resourceTypes.length > 0 && {
        resourceTypes: filter.resourceTypes,
      }),
    ...(filter.resourceTags &&
      filter.resourceTags.length > 0 && {
        resourceTags: filter.resourceTags.map(({ name }) => name),
      }),
    ...(filter.roles &&
      filter.roles.length > 0 && {
        roleUuids: filter.roles.map(({ uuid }) => uuid),
      }),
    ...(((filter.resources && filter.resources.length > 0) ||
      (filter.groups && filter.groups.length > 0)) && {
      resourceUuids: [
        ...(filter.resources ?? []).map(({ uuid }) => uuid),
        ...(filter.groups ?? []).reduce<string[]>(
          (resources, group) => [
            ...resources,
            ...(
              group.resources as (
                | Identifiable<Resource>
                | Identifiable<ProjectResource>
              )[]
            ).map((resource) =>
              "resource" in resource ? resource.resource.uuid : resource.uuid,
            ),
          ],
          [],
        ),
      ],
    }),
  };
}

export type { ListedResource, ListingPage };

export { toListedResource, toListingBody, toOrganizationalUnitsCriteria };
